import {
  DrawdownCalculatorDataRow,
  useDrawdownCalculator,
} from "features/drawdown/DrawdownCalculator";
import { DrawdownFund } from "features/drawdown/DrawdownFund";
import { CreditFacilityLegacyPaydownCreateMutationBody } from "gen/endpoints/credit-facility/credit-facility";
import { AllocationLookupKey } from "gen/model/allocationLookupKey";
import { isNull } from "lodash-es";
import { useCallback, useMemo, useRef } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { tryParseDecimal } from "shared/utils/DecimalUtils";
import { setFormValueIfChanged } from "shared/utils/FormUtils";

export const useCreditFacilityPaydownCalculator = (
  form: UseFormReturn<CreditFacilityLegacyPaydownCreateMutationBody>,
  fundsForRow?: (lookupKey: AllocationLookupKey) => DrawdownFund[],
) => {
  const formRef = useRef(form);
  const watchedFields = useWatch({
    control: form.control,
    name: "uses",
    defaultValue: [],
  });
  useDrawdownCalculator(
    useMemo(
      () =>
        watchedFields.map((field) => ({
          useTypeId: field.use_type,
          isProrata: field.is_prorata,
          allocationLookupKey: {
            transaction_code_id: field.transaction_code_id || null,
            investment_id: field.investment_entity_id || null,
          },
          amount: field.fund_family_amount,
          fundAmounts: Object.fromEntries(
            Object.entries(field.fund_amounts).map(([fundId, fundData]) => [
              fundId,
              tryParseDecimal(fundData.amount) && !isNull(fundData.amount)
                ? fundData.amount
                : null,
            ]),
          ),
        })),
      [watchedFields],
    ),
    useCallback((newFields: DrawdownCalculatorDataRow[]) => {
      const form = formRef.current;
      newFields.forEach((newField, index) => {
        setFormValueIfChanged(
          form,
          `uses.${index}.is_prorata` as const,
          newField.isProrata,
        );
        setFormValueIfChanged(
          form,
          `uses.${index}.fund_family_amount`,
          newField.amount,
        );
        Object.entries(newField.fundAmounts ?? {}).forEach(
          ([fundId, amount]) => {
            setFormValueIfChanged(
              form,
              `uses.${index}.fund_amounts.${fundId}.amount` as "uses.0.fund_amounts.0.amount",
              amount,
            );
          },
        );
      });
    }, []),
    fundsForRow,
  );
};
